import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSyncConfigV1Service, serviceName } from '../service';
import { ListChannelsIO, ServiceQueries } from '../types';
import { useSyncConfigV1Queries } from './use-sync-config-v1-queries';
import { UseSyncConfigV1QueryEndpointArgs, useSyncConfigV1Query } from './use-sync-config-v1-query';

/**
 * A hook that returns a query for the `ListChannels` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListChannelsQuery = <E = unknown, D = ListChannelsIO['output']>(
  args: UseSyncConfigV1QueryEndpointArgs<'ListChannels', E, D>
) =>
  useSyncConfigV1Query({
    endpointName: 'ListChannels',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `ListChannels` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListChannelsQueries = <E = unknown, D = ListChannelsIO['output']>(
  argsArr: UseSyncConfigV1QueryEndpointArgs<'ListChannels', E, D>[]
) =>
  useSyncConfigV1Queries(
    argsArr.map((args) => ({
      endpointName: 'ListChannels',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `ListChannels` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListChannels = () => {
  const queryClient = useQueryClient();

  const fetchListChannels = async <E = unknown, D = ListChannelsIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSyncConfigV1QueryEndpointArgs<'ListChannels', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      ListChannelsIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListChannels'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        ListChannelsIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListChannels'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListChannels'>({
          serviceName,
          endpointName: 'ListChannels',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSyncConfigV1Service.ListChannels(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchListChannels;
};
